import { Tab, TabList, Tabs } from '@chakra-ui/react';
import Features from '@ui/components/Features';
import { LowCroBalanceDrawer } from '@ui/components/LowCroBalanceDrawer';
import { InDesktop, InMobile } from '@ui/components/MobileOrDesktop';
import { SEOTemplate } from '@ui/components/SEO';
import { UseYourTokenDrawer } from '@ui/components/useYourTokenDrawer';
import { pathDict } from '@ui/config/paths';
import { useRealPathname } from '@ui/hooks/router';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { useRedirectFromHiddenPage } from '@ui/hooks/useRedirectFromHiddenPage';
import { useTranslations } from '@ui/i18n';
import { useRouter } from '@ui/overrides/next/router';
import { ZKSYNC_ENABLED } from '@veno-app/wallet';
import type { NextPage } from 'next';

import { StakeAndUseLcroDesktop } from '../StakeAndUseLcro/StakeAndUseLcroDesktop';
import UseLcroDirectly from '../UseLcroDirectly';

export const UseLcro: NextPage = () => {
  const realPathname = useRealPathname();
  const t = useTranslations();
  const router = useRouter();
  const globalCoinMode = useCoinMode();
  const modes = [
    {
      mode: 'stake' as const,
      text: t('Stake {coin}', {
        coin: globalCoinMode.toUpperCase(),
      }),
      routePath: pathDict[globalCoinMode].stake,
    },
    {
      mode: 'use' as const,
      text: t('Use {coin}', {
        coin: COIN_MODE[globalCoinMode].liquidName.toUpperCase(),
      }),
      routePath:
        globalCoinMode === 'cro'
          ? pathDict.cro.useLiquid
          : globalCoinMode === 'eth'
          ? pathDict.eth.useLiquid
          : pathDict.atom.useLiquid,
    },
  ];

  const coinModeInfo = COIN_MODE[globalCoinMode];

  useRedirectFromHiddenPage();
  if (globalCoinMode === 'eth' && !ZKSYNC_ENABLED) return null;

  return (
    <>
      <SEOTemplate
        title={`Use ${coinModeInfo.liquidName} and earn DeFi APY - Veno Finance`}
        description={`Use ${coinModeInfo.liquidName} across the Cronos DeFi Ecosystem to earn additional rewards.`}
      />
      <InMobile>
        <>
          <Tabs
            mb="2px"
            index={modes.findIndex((v) => v.routePath === realPathname)}
            onChange={(index) => {
              const mode = modes[index];
              router.push(mode.routePath);
            }}
            role="heading"
            aria-label={`use ${coinModeInfo.liquidName}`}
            aria-level={1}
          >
            <TabList>
              {modes.map((v) => {
                return <Tab key={v.mode}>{v.text}</Tab>;
              })}
            </TabList>
          </Tabs>
          <UseLcroDirectly showTitle />
        </>
      </InMobile>
      <InDesktop>
        <StakeAndUseLcroDesktop />
      </InDesktop>
      <Features.lowCroBalanceDrawer>
        <LowCroBalanceDrawer />
      </Features.lowCroBalanceDrawer>
      <UseYourTokenDrawer />
    </>
  );
};
